import axios from "axios";
import { Button, OutlineButton, SubtleButton } from "components/button";
import Field from "components/field";
import { useModalContext } from "components/modal";
import { useSnackbarContext } from "components/snackbar";
import CustomDocModal from "containers/customDocModal";
import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { TProperty } from "types/property";
import { useTranslation } from 'react-i18next';

type Props = {
  invoiceId: string;
  fkBillingAddressId?: number | string | null;
};

export const MarkInvoiceAsSentModal = (props: Props) => {
  const { t } = useTranslation();
  const tBase = "views.invoice.markInvoiceAsSentModal";
  const tr = (key: string) => t(`${tBase}.${key}`);
  const snackbar = useSnackbarContext(),
    queryClient = useQueryClient(),
    modal = useModalContext();

  const [templateId, setTemplateId] = React.useState<string>(),
    [completion_notes, setCompletionNotes] = React.useState(""),
    [billing, setBilling] = React.useState<TProperty>(),
    [showAll, setShowAll] = React.useState(false);

  useQuery(
    ["invoice-send-details"],
    async () =>
      await axios
        .get(
          `${process.env.REACT_APP_SERVER_URL}/api/v1/company/invoice/${props.invoiceId}/send-details`
        )
        .then((res) => res.data),
    {
      refetchOnWindowFocus: false,
      onSuccess: (details) => {
        if (details?.sendDetails?.[0]?.fk_customdoc_id) {
          setTemplateId(String(details.sendDetails[0].fk_customdoc_id))
        }
      },
    }
  )

  useQuery<string>(
    ["completion notes"],
    async () =>
      await axios
        .get(
          `
  ${process.env.REACT_APP_SERVER_URL}/api/v1/company/invoice/${props.invoiceId}/completion_notes
  `
        )
        .then((res) => res.data),
    {
      refetchOnWindowFocus: false,
      onSuccess: (str) => {
        setCompletionNotes(str);
      },
    }
  );

  const billingAddress = useQuery<TProperty[] | null>(
    ["billing address", props.invoiceId],
    async () =>
      await axios
        .get(
          `
  ${process.env.REACT_APP_SERVER_URL}/api/v1/company/invoice/${props.invoiceId}/billing_address
  `
        )
        .then((res) => res.data)
  );

  React.useEffect(() => {
    const list = billingAddress.data
    if (!list?.length) return

    if (
      props.fkBillingAddressId === undefined ||
      props.fkBillingAddressId === null ||
      props.fkBillingAddressId === ""
    ) {
      return
    }

    const matched = list.find(
      (address) => String(address.id) === String(props.fkBillingAddressId)
    )

    if (matched) {
      setBilling(matched)
    }
  }, [billingAddress.data, props.fkBillingAddressId])

  const mutation = useMutation(
    async () =>
      await axios
        .post(
          `
  ${process.env.REACT_APP_SERVER_URL}/api/v1/company/invoice/${props.invoiceId}/markSent
  `,
          { templateId, completion_notes, billing }
        )
        .then((res) => res.data),
    {
      onSuccess: () => {
        snackbar.showSnackbar(tr("Invoice Marked Sent!"));
        queryClient.invalidateQueries();
        modal.setModal();
      },
    }
  );

  const preview = useMutation(
    async () =>
      await axios
        .get(
          `${process.env.REACT_APP_SERVER_URL}/api/v1/company/invoice/${props.invoiceId}/preview`,
          {
            params: {
              templateId,
              completion_notes,
              billing,
            },
          }
        )
        .then((res) => res.data),
    {
      onSuccess: (url) => {
        window.open(url, "_blank");
      },
    }
  );

  const handlePreview = () => {
    preview.mutate();
  };

  const visibleAddresses = showAll
    ? billingAddress.data
    : billingAddress.data?.slice(0, 5)

  const populateBillingAddresses = visibleAddresses?.map((address) => {
    return (
      <label key={address.id} className="flex items-center space-x-2 space-y-2">
        <input
          type="radio"
          value={address.id}
          name="billing_address"
          onChange={() => setBilling(address)}
          checked={billing?.id === address.id}
          className="mt-2.5"
        />
        <div className="text-wrap">
          {address.full_address}
          {String(address.id) === String(props.fkBillingAddressId) && (
            <span className="ml-2 rounded bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-800">
              {tr("Default billing")}
            </span>
          )}
          {address.name && (
            <span className="text-gray-500 italic"> ({address.name})</span>
          )}
        </div>
      </label>
    )
  })

  return (
    <div className="max-w-[500px] space-y-4">
      <CustomDocModal
        id={templateId}
        type="invoice"
        onClick={(id) => setTemplateId((tId) => (tId === id ? undefined : id))}
      />
      <div>
        <h3>{tr("Billing Address")}</h3>
        <div className="flex flex-col">{populateBillingAddresses}</div>
      </div>
      <div className="flex justify-center">
        {billingAddress && (billingAddress.data ?? []).length > 5 && (
          <button
            type="button"
            onClick={() => setShowAll(!showAll)}
            className="text-sm text-blue-600 hover:underline"
          >
            {showAll ? tr("Show Less") : tr("Show More")}
          </button>
        )}
      </div>
      <div>
        <h3>{tr("Completion Notes")}</h3>
        <div className="flex flex-col">
          <Field
            type="textarea"
            value={completion_notes}
            onChange={(e) => setCompletionNotes(e.target.value)}
            minRows={5}
          />
        </div>
      </div>
      <div></div>
      <div className="flex justify-end space-x-2">
        <SubtleButton
          isLoading={preview.isLoading}
          disabled={!templateId}
          onClick={handlePreview}
        >
          {tr("Preview")}
        </SubtleButton>
        <Button
          primary
          type="submit"
          isLoading={mutation.isLoading}
          disabled={!templateId}
          onClick={() => mutation.mutate()}
        >
          {tr("Mark Sent")}
        </Button>
        <OutlineButton onClick={() => modal.setModal()}>{tr("Cancel")}</OutlineButton>
      </div>
    </div>
  );
};

// const { mutate: markSent } = useMutation(
//   async () =>
//     await axios
//       .post(
//         `${process.env.REACT_APP_SERVER_URL}/api/v1/company/invoice/${id}/markSent
//   `
//       )
//       .then((res) => res.data),
//   {
//     onSuccess: () => {
//       snackBar.showSnackbar("Invoice Marked Sent!");
//       queryClient.invalidateQueries();
//     },
//   }
// );

// const handleMarkSent = () => {
//   markSent();
// };
